import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
  Grid,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Checkbox,
  Box,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomDropzone from "components/Dropzone";
import {
  GradientButton,
  GradientButtonOutline,
} from "components/GradientButton";
import api from "_api";
import Swal from "sweetalert2";
import "assets/css/modal.css";
import SaveIcon from "assets/images/SaveIcon.svg";
import { Switch } from "antd";
import { useAuth } from "hooks/useAuth";

const ModalCreate = ({
  open,
  onClose,
  selectedProduct,
  fetchProductData,
  companyId,
}) => {
  const [files1, setFiles1] = useState([]);
  const [files2, setFiles2] = useState([]);
  const [error1, setError1] = useState(null);
  const [initialProductUrls, setInitialProductUrls] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const [fetchData, setFetchData] = useState([]);
  const { user } = useAuth();
  console.log("user", user);

  // Validation schema
  const validationSchema = Yup.object({
    username: Yup.string().required("โปรดระบุผู้ใช้งาน"),
    name: Yup.string().required("โปรดระบุชื่อ-นามสกุล"),
    password: Yup.string()
      .required("โปรดระบุรหัสผ่าน")
      .min(8, "ไม่สามารถน้อยกว่า 8 ตัวอักษร"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "รหัสผ่านไม่ตรงกัน")
      .required("กรุณากรอกยืนยันรหัสผ่าน"),
    role_id: Yup.object().required("กรุณาเลือกสิทธิ์"),
  });

  const updateUserValidationSchema = Yup.object({
    username: Yup.string().required("โปรดระบุผู้ใช้งาน"),
    name: Yup.string().required("โปรดระบุชื่อ-นามสกุล"),
    role_id: Yup.object().required("กรุณาเลือกสิทธิ์"),
  });

  const roleOption = [
    ...(selectedProduct?.role_id == 1
      ? [{ label: "แอดมิน", id: 1, disabled: true }]
      : []),
    { label: "เจ้าของร้าน", id: 2 },
    {
      label: "หัวหน้าแอดมินการอนุมัติ",
      id: 3,
      disabled: user?.role !== "admin",
    },
    {
      label: "หัวหน้าแอดมินการจัดการ",
      id: 4,
      disabled: user?.role !== "admin",
    },
    { label: "แอดมินการอนุมัติ", id: 5 },
    { label: "แอดมินการจัดการ", id: 6 },
  ];

  const productFields = [
    {
      label: "ชื่อผู้ใช้งาน",
      name: "username",
      type: "text",
      disabled: selectedProduct ? true : false,
    },
    {
      label: "ชื่อ-นามสกุล",
      name: "name",
      type: "text",
      required: true,
    },
    ...(selectedProduct
      ? [
          {
            label: "ชื่อร้าน/บริษัท",
            name: "Company",
            type: "text",
            disabled: true,
          },
        ]
      : []),
    ...(!selectedProduct
      ? [
          {
            label: "รหัสผ่าน",
            name: "password",
            type: "password",
            required: true,
          },
          {
            label: "ยืนยันรหัสผ่าน",
            name: "confirmPassword",
            type: "password",
            required: true,
          },
        ]
      : []),
    {
      label: "สิทธิ์ที่สร้าง",
      name: "role_id",
      type: "autocomplete",
      required: true,
    },
    {
      label: "สถานะ",
      name: "status",
      type: "checkbox",
      required: false,
    },
  ];

  const handleImageDelete = (imageId) => {
    setDeletedImageIds(imageId);
  };

  // console.log("checking picture display in modal", initialProductUrls);
  useEffect(() => {
    if (selectedProduct) {
      formik.setValues({
        username: selectedProduct.username || "",
        name: selectedProduct.name || "",
        role_id:
          roleOption.find((role) => role.id === selectedProduct.role_id) || 2,
        status: selectedProduct.status ?? false,
      });

      // Set initial image URLs
      if (selectedProduct.image) {
        setInitialProductUrls([
          { picture: selectedProduct.image, id: selectedProduct.user_id },
        ]);
      }
    }
  }, [selectedProduct]);

  // Function to transform object to FormData
  const tranFormObjectToFormData = (obj) => {
    const formData = new FormData();

    Object.entries(obj).forEach(([key, value]) => {
      if (key === "company_type_id" && typeof value === "object" && value.id) {
        formData.append(key, value.id);
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (typeof item === "object" && item !== null) {
            formData.append(`${key}[${index}]`, JSON.stringify(item));
          } else {
            formData.append(`${key}[${index}]`, item);
          }
        });
      } else if (key === "files" && Array.isArray(value)) {
        value.forEach((file, index) => {
          formData.append(`files[${index}]`, file);
        });
      } else {
        formData.append(key, value !== undefined ? value : ""); // Ensure undefined values are not passed
      }
    });

    // Log formData for debugging
    for (let pair of formData.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }

    return formData;
  };

  // console.log("updatecompanyData", deletedImageIds);

  // Formik setup
  const formik = useFormik({
    initialValues: {
      username: "",
      name: "",
      password: "",
      confirmPassword: "",
      role_id: roleOption.find((option) => option.id) || null, // Set default value
      status: false,
    },
    validationSchema: selectedProduct
      ? updateUserValidationSchema
      : validationSchema,

    onSubmit: async (values, { resetForm }) => {
      if (selectedProduct) {
        //Updated
        const userData = {
          username: values.username,
          name: values.name,
          role_id: values.role_id?.id || null,
          status: values.status,
          files: files1.map((fileObj) => fileObj.file)[0],
        };
        try {
          const userFormData = tranFormObjectToFormData(userData);

          const userResponse = await api.super_admin.updateUser(
            selectedProduct.user_id,
            userFormData
          );

          if (userResponse.isStatus) {
            resetForm();
            onClose();
            fetchProductData();
          }
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: `${
              error.status == 403
                ? "สิทธิ์คุณไม่เพียงพอ"
                : "มีบางอย่างผิดพลาดกรุณาแจ้งซัพพอร์ต"
            }`,
            text: `${
              error.status == 403
                ? "สิทธิ์คุณไม่เพียงพอในการสร้างบริษัท"
                : "มีบางอย่างผิดพลาดกรุณาแจ้งซัพพอร์ต"
            }`,
            timer: 3000,
            showConfirmButton: false,
            customClass: {
              container: "swal2-container-custom",
            },
            didOpen: () => {
              document.querySelector(
                ".swal2-container-custom"
              ).style.zIndex = 1500;
            },
          });
        }
      } else {
        // Created
        const userData = {
          username: values.username,
          name: values.name,
          password: values.password || null,
          role_id: values.role_id?.id || null,
          status: values.status,
          files: files1.map((fileObj) => fileObj.file)[0],
          company_id: companyId,
        };

        // Proceed with your API call
        //
        try {
          const userFormData = tranFormObjectToFormData(userData);

          const userResponse = await api.super_admin.createUser(userFormData);

          if (userResponse.isStatus) {
            resetForm();
            onClose();
            fetchProductData();
          }
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: `${
              error.status == 403
                ? "สิทธิ์คุณไม่เพียงพอ"
                : "มีบางอย่างผิดพลาดกรุณาแจ้งซัพพอร์ต"
            }`,
            text: `${
              error.status == 403
                ? "สิทธิ์คุณไม่เพียงพอในการแก้ไขผู้ใช้งาน"
                : "มีบางอย่างผิดพลาดกรุณาแจ้งซัพพอร์ต"
            }`,
            timer: 3000,
            showConfirmButton: false,
            customClass: {
              container: "swal2-container-custom",
            },
            didOpen: () => {
              document.querySelector(
                ".swal2-container-custom"
              ).style.zIndex = 1500;
            },
          });
        }
      }
    },
  });

  const handleCancel = () => {
    const formHasUnsavedChanges = Object.keys(touched).some(
      (field) => touched[field]
    );

    if (formHasUnsavedChanges) {
      Swal.fire({
        html: `
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <!-- Left side icon/picture -->
            <div style="margin-right: 20px;">
              <img src="${SaveIcon}" alt="warning icon" width="300" height="300"/>
            </div>
      
            <!-- Right side text and buttons -->
            <div style="flex: 1;">
              <h2 style="font-size: 24px; color: #333;">ยืนยันการบันทึกหรือไม่</h2>
              <div id="custom-buttons" style="display: flex; justify-content: flex-end; margin-top: 20px;">
                <!-- React buttons will be injected here -->
              </div>
            </div>
          </div>
        `,
        showCancelButton: false, // Disable default buttons
        showConfirmButton: false, // Disable default buttons
        customClass: {
          container: "swal2-container-custom",
        },
        didOpen: () => {
          document.querySelector(".swal2-container-custom").style.zIndex = 1500;

          // Render your React button components after the Swal modal opens
          ReactDOM.render(
            <>
              <GradientButton
                onClick={() => {
                  // Handle cancel behavior
                  Swal.close(); // Close Swal
                }}
                style={{ width: "100%", marginBottom: "10px" }} // Make full width
              >
                ยกเลิก
              </GradientButton>
              <GradientButtonOutline
                onClick={() => {
                  // Close the current Swal first before showing the confirmation Swal
                  Swal.close();
                  formik.resetForm();
                  setFiles1([]); // Clear product images
                  setFiles2([]); // Clear customer images
                  setInitialProductUrls([]); // Clear initial product image URLs
                  setDeletedImageIds([]); // Clear deleted image IDs
                  onClose(); // Close the modal
                }}
                style={{ width: "100%" }}
              >
                ตกลง
              </GradientButtonOutline>
            </>,
            document.getElementById("custom-buttons") // Inject React components into this div
          );
        },
      });
    } else {
      formik.resetForm();
      setFiles1([]); // Clear product images
      setFiles2([]); // Clear customer images
      setInitialProductUrls([]); // Clear initial product image URLs
      // setInitialCustomerUrls([]);
      setDeletedImageIds([]);
      onClose();
    }
  };

  const renderFields = (fields) => {
    return fields.map((field, index) => (
      <React.Fragment key={index}>
        <Grid item xs={12} sm={2}>
          <Typography variant="subtitle1">
            {field.label}
            {field.required && (
              <span style={{ marginLeft: "4px", color: "red" }}>*</span>
            )}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={10}>
          {field.type === "autocomplete" ? (
            <Autocomplete
              id={field.name}
              options={roleOption}
              getOptionLabel={(option) => option.label}
              value={formik.values.role_id}
              onChange={(event, newValue) => {
                if (!field.disabled) {
                  formik.setFieldValue("role_id", newValue);
                }
              }}
              disabled={user.id === selectedProduct?.user_id}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{
                ".MuiAutocomplete-inputRoot": {
                  height: "40px",
                  padding: "0px 0px 0px 5px !important",
                },
              }}
              renderOption={(props, option) => (
                <li
                  {...props}
                  style={{
                    display: option.disabled ? "none" : "block", // ซ่อนตัวเลือกที่ถูกปิดใช้งาน
                  }}
                >
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name={field.name}
                  error={
                    formik.touched[field.name] &&
                    Boolean(formik.errors[field.name])
                  }
                  helperText={
                    formik.touched[field.name] && formik.errors[field.name]
                  }
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    sx: { height: "40px" },
                  }}
                />
              )}
            />
          ) : field.type === "checkbox" ? (
            <Switch
              value={formik.values.status}
              onChange={(checked) => {
                if (!field.disabled) {
                  formik.setFieldValue("status", checked);
                }
              }}
            />
          ) : field.name === "Company" ? (
            <TextField
              disabled={field.disabled}
              id={field.name}
              name={field.name}
              type={field.type}
              value={selectedProduct?.Company?.company_name || ""}
              fullWidth
              InputProps={{
                sx: { height: "40px" },
              }}
            />
          ) : (
            <TextField
              disabled={field.disabled}
              id={field.name}
              name={field.name}
              type={field.type}
              value={formik.values[field.name]}
              onChange={formik.handleChange}
              {...getFieldProps(field.name)}
              error={
                formik.touched[field.name] && Boolean(formik.errors[field.name])
              }
              helperText={
                formik.touched[field.name] && formik.errors[field.name]
              }
              fullWidth
              InputProps={{
                sx: { height: "40px" },
                inputProps: {
                  style: {
                    "-moz-appearance": "textfield", // Hide up/down arrows in Firefox
                    "-webkit-appearance": "none", // Hide up/down arrows in Chrome/Safari
                    appearance: "none", // Hide up/down arrows in other browsers
                  },
                  // maxLength:
                  //   field.name === "amphure_id"
                  //     ? 13
                  //     : field.name === "district_id"
                  //     ? 10
                  //     : undefined, // Set max length
                  inputMode: field.type === "text" ? "numeric" : undefined, // Ensure numeric keyboard on mobile
                  pattern: field.type === "text" ? "[0-9]*" : undefined, // Restrict input to numbers
                },
              }}
            />
          )}
        </Grid>
      </React.Fragment>
    ));
  };

  const { errors, touched, getFieldProps, handleSubmit } = formik;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      sx={{
        borderRadius: "8px",
      }}
    >
      <DialogTitle>
        <Typography variant="h4">
          {selectedProduct ? "แก้ไขผู้ใช้งาน" : "สร้างผู้ใช้งาน"}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3} sx={{ padding: 3 }}>
            <Grid item xs={12}>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {renderFields(productFields)}

                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    รูปโปรไฟล์
                  </Typography>
                  <CustomDropzone
                    files={files1}
                    initialUrls={initialProductUrls}
                    setFiles={setFiles1}
                    setError={setError1}
                    onDelete={handleImageDelete}
                    maxImages={1}
                  />
                  {error1 && (
                    <Typography variant="caption" color="error">
                      {error1}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <GradientButtonOutline
          variant="outlined"
          sx={{ mr: 1 }}
          onClick={handleCancel}
        >
          ยกเลิก
        </GradientButtonOutline>
        <GradientButton
          variant="contained"
          onClick={formik.handleSubmit}
          type="submit"
        >
          บันทึก
        </GradientButton>
      </DialogActions>
    </Dialog>
  );
};

export default ModalCreate;
